import {
	OnInit,
	Component,
	ElementRef,
	Input,
	ViewChild,
	OnDestroy,
} from '@angular/core';
import {
	AbstractControl,
	UntypedFormGroup,
	ValidationErrors,
} from '@angular/forms';
import { getCurrencySymbol } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import _ from 'lodash-es';
import {
	Asset,
	AssetKey,
	type CompanyKey,
	DynamicChoiceKey,
	EditItemType,
	IsArrayProperty,
	LocalizedTextIds,
	Mode,
	ReferenceValue,
	UpdateOptions,
	getAssetUpdateOptions,
	getCompanyUpdateOptions,
} from 'company-finder-common';

import {
	MultiChoiceItemChange,
	SelfUpdateMode,
	ReviewableUpdateField,
	ItemReviewModification,
	ItemReviewModificationType,
	EditItemChoice,
} from '../../company-update.interface.js';

import { LogoSize } from '@Common';
import { ReviewEditsService } from '../../services/review-edits.service.js';
import { CompanyService } from '@Common';
import { CompanyUpdateService } from '../../services/company-update.service.js';
import { UpdateComponentBase } from '../../UpdateComponentBase.js';
import { DeploymentContext } from '@Common';
import {
	areObjectsDifferent,
	arrayIsDifferent,
	itemIsDifferent,
} from '../../utils/diff.js';

// FUTURE: Externalize maxUploadLogoImageSize to config file if desired
// Note: if request payload goes over current limit we set at 10mb,
// need to increase bodyParser.json.limit and bodyParser.urlencoded.limit in server.ts
// Current Salesforce limit is 128KB of base64 text (= 96KB of binary).  They will downsample
// files larger than that, but only up to 1 MB, and we have seen this process fail, so limit
// to 96KB.
const maxUploadLogoImageSize = 96 * 1024; // 96kb

@Component({
	selector: 'edit-item',
	templateUrl: './edit-item.component.html',
	styleUrls: ['./edit-item.component.scss'],
	standalone: false,
})
export class EditItemComponent
	extends UpdateComponentBase
	implements OnInit, OnDestroy
{
	public constructor(
		dc: DeploymentContext,
		private _reviewEditsService: ReviewEditsService,
		_companyUpdateService: CompanyUpdateService,
		_companyService: CompanyService
	) {
		super(dc, _companyUpdateService, _companyService);
	}

	private _mode: Mode = Mode.Company;

	@Input()
	public get mode(): Mode {
		return this._mode;
	}
	public set mode(value: Mode) {
		this._mode = value;
	}

	public updateOptions: UpdateOptions;

	public get asset(): Asset {
		return this.updateService.assetToModify;
	}

	public set asset(value: Asset) {
		this.updateService.assetToModify = value;
	}

	public async ngOnInit(): Promise<void> {
		this.updateOptions = this.inCompanyMode
			? getCompanyUpdateOptions(this.propertyName as CompanyKey)
			: getAssetUpdateOptions(this.propertyName as AssetKey);

		if (this.updateOptions.editType === this.EditItemType.MultiModal) {
			this.allTags = (await this.companyService.getTags())?.map((tag) =>
				tag.toLowerCase()
			);
		}
		// If this isn't a control that we manage the length on, no point registering a subscription
		if (this.canLimitLength) {
			this.lengthSubscription = this.formControl?.valueChanges.subscribe(
				(value) => {
					this.currentLength = value?.length ?? 0;
				}
			);
		}
	}

	public async ngOnDestroy(): Promise<void> {
		this.lengthSubscription?.unsubscribe();
	}

	public get currencySymbol(): string {
		return this.currency
			? getCurrencySymbol(this.currency, 'narrow')
			: getCurrencySymbol('USD', 'narrow');
	}

	public get numericValue(): string {
		if (!this.isEmptyValue && this.type === EditItemType.Currency) {
			return `${this.currencySymbol}${this.currentValue} ${this.currency}`;
		}

		return this.currentValue;
	}

	public get id(): string {
		return `company-update-${this.propertyName}`;
	}

	public get isReviewMode(): boolean {
		return this.selfUpdateMode === SelfUpdateMode.Review;
	}

	public get isEditMode(): boolean {
		return this.selfUpdateMode === SelfUpdateMode.Edit;
	}

	public get hasReviewableField(): boolean {
		return this.isReviewMode && this.isReviewableUpdateFieldSet;
	}

	public get isThisUpdateFieldSet(): boolean {
		return this.isUpdateFieldSet(this.propertyName);
	}

	public get isEditingThisProperty(): boolean {
		return this.isEditingProperty[this.propertyName];
	}

	public get showApproveDecline(): boolean {
		// Assets are approved/declined as a whole, like deals and funding
		return (
			this.inCompanyMode && this.isReviewMode && this.isThisUpdateFieldSet
		);
	}

	public get isAvailableToEdit(): boolean {
		if (
			this.isReadOnly ||
			(this.isReviewMode && this.type === EditItemType.MultiModal)
		) {
			return false;
		}

		return (
			this.isEditMode ||
			(this.isReviewMode &&
				this.isReviewableUpdateFieldSet &&
				!this.isPropertyDeclined)
		);
	}

	public get isEmptyValue(): boolean {
		return (
			this.currentValue === null ||
			this.currentValue === undefined ||
			this.currentValue === ''
		);
	}

	public get showOrHideOriginalText(): string {
		return this.Loc(
			this.isShowingOriginalText
				? LocalizedTextIds.HideOriginal
				: LocalizedTextIds.ViewOriginal
		);
	}

	public get isInactive(): boolean {
		return (
			this._reviewEditsService.currentEditItemProperty &&
			this._reviewEditsService.currentEditItemProperty !==
				this.propertyName
		);
	}

	public get isPropertyDeclined(): boolean {
		return (
			!this.isReadOnly &&
			this._reviewEditsService.isPropertyDeclined(this.propertyName)
		);
	}

	public get isReviewableUpdateFieldSet(): boolean {
		return this.reviewableUpdateFields && this.isThisUpdateFieldSet;
	}

	public get showFlagIcon(): boolean {
		return (
			this.isReviewableUpdateFieldSet &&
			this._reviewEditsService.isPropertyInNeedOfReview(this.propertyName)
		);
	}

	public get showRevertIcon(): boolean {
		if (!this.isAvailableToEdit) {
			return false;
		}

		if (this.isEditingThisProperty) {
			return true;
		}
		// NOTE: Tag reversion is handled differently, and does not show the revert icon.
		const companyToCompare =
			this.isEditMode && !this.reviewableUpdateFields
				? this.companyBeforeAnyChanges
				: this.companyWithPending;

		if (this.inCompanyMode) {
			return itemIsDifferent(
				this.propertyName,
				this.company,
				companyToCompare
			);
		}

		const newProp = this.company.assets.find(
			(asset) => asset.assetId === this.asset.assetId
		)[this.propertyName];
		const origProp = companyToCompare.assets.find(
			(asset) => asset.assetId === this.asset.assetId
		)[this.propertyName];

		if (IsArrayProperty(this.propertyName as AssetKey, Asset)) {
			return arrayIsDifferent(newProp, origProp);
		}

		return areObjectsDifferent(newProp, origProp);
	}

	public get reviewableUpdateFields(): ReviewableUpdateField[] {
		return this.inCompanyMode
			? this.updateForReview.updateFields
			: this.updateForReview.assetUpdates[this.asset.assetId]
					?.updateFields;
	}

	public get showViewOriginalButton(): boolean {
		// FUTURE: it might be nice if this returned "false" after reverting (or otherwise undoing) an edit.
		// But getting that right seemed to be non-trivial since even after a revert, the reviewableUpdateFields is still `isSet = true` and I think it gets pruned later.
		// It's possible a compare of new/old values could be added to inform this, but the data structures are complicated enough
		// that there didn't seem to be a super easy way to do that for companies & assets, and for Company Users and Reviewers.
		return (
			!this.isEditingThisProperty &&
			!this.isReadOnly &&
			this.type !== EditItemType.Logo &&
			this.type !== EditItemType.MultiModal &&
			this.isReviewableUpdateFieldSet
		);
	}

	public get originalValue(): string {
		return (
			(this.inCompanyMode
				? this.companyBeforeAnyChanges[this.propertyName]?.toString()
				: this.companyBeforeAnyChanges.assets.find(
						(asset) => asset.assetId === this.asset.assetId
					)[this.propertyName]
			)?.toString() ?? ''
		);
	}

	public get originalValueDisplay(): string {
		return this.originalValue != undefined && this.originalValue !== ''
			? this.selectedChoiceOriginalVal
			: this.Loc(LocalizedTextIds.NoPreviousValue);
	}

	public get currentValue(): string {
		return this.inCompanyMode
			? this.company[this.propertyName]?.toString()
			: this.asset[this.propertyName]?.toString();
	}

	public set currentValue(newVal) {
		if (this.inCompanyMode) {
			this.company[this.propertyName] = newVal;
		} else {
			this.asset[this.propertyName] = newVal;
		}
	}

	public get currentArrValue(): string[] {
		return this.inCompanyMode
			? this.company[this.propertyName]
			: this.asset[this.propertyName];
	}

	public set currentArrValue(newVal) {
		if (this.inCompanyMode) {
			this.company[this.propertyName] = newVal;
		} else {
			this.asset[this.propertyName] = newVal;
		}
	}

	public get origValues(): string[] {
		return this.inCompanyMode
			? this.companyBeforeAnyChanges[this.propertyName]
			: this.companyBeforeAnyChanges.assets.find(
					(asset) => asset.assetId === this.asset.assetId
				)[this.propertyName];
	}

	public get itemsAdded(): string[] {
		return this.currentArrValue.filter(
			(item) => !this.origValues.includes(item)
		);
	}

	public get itemsRemoved(): string[] {
		return this.origValues.filter(
			(item) => !this.currentValue.includes(item)
		);
	}

	public get itemsUnchanged(): string[] {
		return this.currentArrValue.filter((item) =>
			this.origValues.includes(item)
		);
	}

	public allTags: string[] = [];
	public isShowingOriginalText = false;

	public get labelText(): string {
		return this.getHeaderForProperty(this.propertyName);
	}

	public get hidden(): boolean {
		return this.updateOptions.preconditionProp
			? this.inCompanyMode
				? !this.company[this.updateOptions.preconditionProp]
				: !this.asset[this.updateOptions.preconditionProp]
			: false;
	}

	public get labelDetail(): string {
		return this.Loc(this.updateOptions.labelTextId);
	}

	public get prompt(): string {
		return this.Loc(this.updateOptions.promptTextId);
	}

	public get footnote(): string {
		return this.Loc(this.updateOptions.footnoteId);
	}

	public get addText(): string {
		return `${this.Loc(LocalizedTextIds.Add)} ${this.labelText}`;
	}

	public get removeText(): string {
		return this.Loc(LocalizedTextIds.TheseItemsRemoved, this.labelText);
	}

	public get addedText(): string {
		return this.Loc(LocalizedTextIds.TheseItemsAdded, this.labelText);
	}

	public get showFootnote(): boolean {
		return (
			this.updateOptions.footnoteCountries?.includes(
				this.company.countryForDeiReporting
			) && !!this.footnote
		);
	}

	public get tooltip(): string {
		return this.Loc(this.updateOptions.tooltipId);
	}

	public get subtitle(): string {
		return this.Loc(this.updateOptions.subtitleId);
	}

	public get type(): EditItemType {
		return this.updateOptions.editType ?? EditItemType.Default;
	}

	public get isNumericType(): boolean {
		return [
			EditItemType.Numeric,
			EditItemType.Year,
			EditItemType.Currency,
		].includes(this.type);
	}

	public get currency(): string {
		return 'USD';
	}

	public get maxLength(): number {
		if (this.type === EditItemType.Year) {
			return 4;
		}
		return this.updateOptions.maxLength ?? 32768;
	}

	public get max(): number {
		if (this.type === EditItemType.Year) {
			return 9999;
		}
	}

	public get step(): number | undefined {
		return this.type === EditItemType.Year ? 1 : undefined;
	}

	public get pattern(): string | undefined {
		return this.type === EditItemType.Year ? '\\d*' : undefined;
	}

	public get showLength(): boolean {
		return this.updateOptions.showLength;
	}

	public get isJnjConfidentialInfo(): boolean {
		return this.updateOptions.isJnjConfidentialInfo;
	}

	public get isReadOnly(): boolean {
		return this.updateOptions.isReadOnly;
	}

	public get selectedMultiChoices(): string[] {
		const formControl = this.parentForm.get(this.propertyName);
		const selectedChoices = this.parseMultipleChoices(
			formControl.value?.toString(),
			','
		);
		return selectedChoices;
	}

	public get selectedChoiceVal(): string {
		const key =
			this.valueList.find(
				(opt) => opt.value === this.currentValue?.toString()
			)?.label ?? this.currentValue;

		return this.LocDbValue(key, true);
	}

	public get selectedChoiceOriginalVal(): string {
		const key =
			this.valueList.find(
				(opt) => opt.value === this.originalValue?.toString()
			)?.label ?? this.originalValue;

		return this.LocDbValue(key, true);
	}

	public get dynamicChoices(): ReferenceValue[] {
		// FUTURE (ADJQ-1993) - If we ever re-enable sector editing
		// (which seems unlikely, as that was only ever for Universal, which now
		// derives sectors from assets) the subsector and R&D stage logic will need
		// to be revisted
		switch (this.updateOptions.dynamicChoiceKey) {
			case DynamicChoiceKey.LeadershipDiversity:
				return this.getDeiDiversityChoices(false);
			case DynamicChoiceKey.BoardAdvisorDiversity:
				return this.getDeiDiversityChoices(true);
			case DynamicChoiceKey.RDStage:
				return this._deploymentContext.referenceValueData
					.rdStagesBySector[this.company.primarySector[0]];
			case DynamicChoiceKey.PrimarySubsector:
				return this._deploymentContext.referenceValueData.subsectors[
					this.company.primarySector[0]
				];
			case DynamicChoiceKey.SecondarySubsector:
				return this._deploymentContext.referenceValueData.subsectors[
					this.company.secondarySector
				];
			case DynamicChoiceKey.Tags:
				return this.allTags.map((tag) => ({ value: tag }));
			// FUTURE - currently all of the dynamic choices are either asset
			// or company specific, so we can break them out this way. If that
			// changes this logic will need to check the mode as well
			case DynamicChoiceKey.FocusArea:
				return this._deploymentContext.referenceValueData.focusAreas[
					this.asset.sector
				];
			case DynamicChoiceKey.DevelopmentStage:
				return this._deploymentContext.referenceValueData
					.developmentStages[this.asset.sector];
			default:
				return null;
		}
	}

	private getDeiDiversityChoices(forBoardAdvisor: boolean): EditItemChoice[] {
		const options = this.getDiversityOptionsForCountry(
			this.company.countryForDeiReporting
		);

		if (!options) {
			return;
		}

		// This set will be treated like EditItemChoice objects and hence we need
		// copies to avoid unexpected shared state.
		const choices = _.clone(options);

		// Exclude choices that are specific to Board of Advisor, unless this is for the board question
		return choices.filter(
			(choice) => !choice.isOnlyForBoardAdvisor || forBoardAdvisor
		);
	}

	public parseMultipleChoices(
		separatedValueString: string,
		separator = ';'
	): string[] {
		try {
			// Make sure they are sorted, so the diffs can reliably match sets with the same options.
			return separatedValueString?.split(separator)?.sort() ?? [];
		} catch {
			return [];
		}
	}

	public get valueList(): ReferenceValue[] {
		return (
			this.dynamicChoices ?? this.keyedReferenceData ?? this.yesNoChoices
		);
	}

	private get keyedReferenceData(): ReferenceValue[] {
		// Even though we only load data this way when it is an array
		// of ReferenceValues, typescript does not know we will never
		// have a referenceDataKey that will only every reference
		// certain fields and there is no simple way to enforce that,
		// so we cast it here.
		return (
			(this._deploymentContext.referenceValueData[
				this.updateOptions.referenceDataKey
			] as ReferenceValue[]) ?? null
		);
	}

	private readonly yesNoChoices = [
		{ label: '', value: '' },
		{ label: this.Loc(LocalizedTextIds.Yes), value: true.toString() },
		{ label: this.Loc(LocalizedTextIds.No), value: false.toString() },
	];

	public get valuesAsStrings(): string[] {
		return this.valueList.map((val) => val.value);
	}

	public logoSize: LogoSize = LogoSize.Large;
	@ViewChild('logoBase64chooser')
	public logoBase64chooser: ElementRef;
	public maxUploadLogoImageSize = maxUploadLogoImageSize;
	@Input()
	public parentForm: UntypedFormGroup;
	@Input()
	public propertyName: CompanyKey | AssetKey;
	public itemsToExclude: string[] = [];
	public itemsToPreserve: string[] = [];

	public get formControl(): AbstractControl {
		return this.parentForm.get(this.propertyName);
	}
	public currentLength = 0;

	public isShowingTooltip = false;
	public showMultiPickerModal = false;

	public makeEditableSubject = new Subject<{
		propertyName: string;
		ev: MouseEvent;
	}>();
	public revertSubject = new Subject<string>();
	public itemReviewSubject = new Subject<ItemReviewModification>();
	public constrainedValueChangeSubject = new Subject<string>();
	public multiChoiceValueChangedSubject =
		new Subject<MultiChoiceItemChange>();

	public bytesToHumanReadableDisplayString(
		nBytes: number,
		kSize: number = 1024
	): string {
		const units = [
			this.Loc(LocalizedTextIds.Bytes),
			this.Loc(LocalizedTextIds.KB),
			this.Loc(LocalizedTextIds.MB),
			this.Loc(LocalizedTextIds.GB),
			this.Loc(LocalizedTextIds.TB),
			this.Loc(LocalizedTextIds.PB),
			this.Loc(LocalizedTextIds.EB),
			this.Loc(LocalizedTextIds.ZB),
			this.Loc(LocalizedTextIds.YB),
		];

		let i = 0;
		for (i = 0; nBytes > kSize; ++i) {
			nBytes /= kSize;
		}

		return Math.max(nBytes, 0.1).toFixed(i ? 1 : 0) + ' ' + units[i];
	}

	public isCheckedMultiChoice(value: string): boolean {
		return !!this.selectedMultiChoices?.find((item) => item === value);
	}

	public stopEventPropagationDuringEditing(
		ev: MouseEvent,
		onlyIfEditing?: boolean
	): void {
		if (!onlyIfEditing || this.isEditingThisProperty) {
			ev.stopPropagation();
		}
	}

	public isUpdateFieldSet(propertyName: string): boolean {
		return (
			this.reviewableUpdateFields[propertyName] &&
			this.reviewableUpdateFields[propertyName].isSet
		);
	}

	public arraysAreIdentical(a: string | [], b: string | []): boolean {
		a = a !== '' ? (a as []) : [];
		b = b !== '' ? (b as []) : [];
		return (
			a?.every((val, i) => val === b && b[i]) &&
			b?.every((val, i) => val === a && a[i])
		);
	}

	public launchLogoChooser(ev: MouseEvent): void {
		this.stopEventPropagationDuringEditing(ev);
		this.logoBase64chooser.nativeElement.click();
	}

	public launchMultiPickerModal(ev: MouseEvent): void {
		this.makePropertyEditable(ev);
		this.showMultiPickerModal = true;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public onConstrainedValueChange(value: any): void {
		this.constrainedValueChangeSubject.next(value.value);
	}

	public onMultiChoiceItemChange(value: string): void {
		this.multiChoiceValueChangedSubject.next({
			propertyName: this.propertyName,
			value: value,
		});
	}

	public addItems(items: string[]): void {
		this.showMultiPickerModal = false;

		const newItems = this.currentArrValue;
		newItems.push(
			...items.filter((item) => !this.currentArrValue.includes(item))
		);

		this.currentArrValue = newItems;
		this.parentForm.controls[this.propertyName].setValue(newItems);
	}

	public removeItem(item: string, ev: MouseEvent): void {
		this.stopEventPropagationDuringEditing(ev);
		this.currentArrValue = this.currentArrValue.filter((i) => i !== item);
		this.parentForm.controls[this.propertyName].setValue(
			this.currentArrValue
		);
	}

	public toggleItemExclusion(item: string, ev: MouseEvent): void {
		this.stopEventPropagationDuringEditing(ev);
		if (this.isExcludedItem(item)) {
			this.itemsToExclude = this.itemsToExclude.filter((i) => i !== item);
			this.itemReviewSubject.next({
				item: item,
				modificationType: ItemReviewModificationType.Exclude,
				value: false,
			});
		} else {
			this.itemsToExclude.push(item);
			this.itemReviewSubject.next({
				item: item,
				modificationType: ItemReviewModificationType.Exclude,
				value: true,
			});
		}
		this._reviewEditsService.resetPropertyStatus(this.propertyName);
	}

	public get allowPreservation(): boolean {
		if (!this.updateOptions.preconditionProp) {
			return true;
		}

		// If we are changing the parent property, we cannot restore items from a
		// different set.
		return itemIsDifferent(
			this.updateOptions.preconditionProp,
			this.company,
			this.companyBeforeAnyChanges
		);
	}

	public toggleItemPreservation(item: string, ev: MouseEvent): void {
		this.stopEventPropagationDuringEditing(ev);
		if (this.isPreservedItem(item)) {
			this.itemsToPreserve = this.itemsToPreserve.filter(
				(i) => i !== item
			);
			this.itemReviewSubject.next({
				item: item,
				modificationType: ItemReviewModificationType.Preserve,
				value: false,
			});
		} else {
			this.itemsToPreserve.push(item);
			this.itemReviewSubject.next({
				item: item,
				modificationType: ItemReviewModificationType.Preserve,
				value: true,
			});
		}
		this._reviewEditsService.resetPropertyStatus(this.propertyName);
	}

	public isExcludedItem(item: string): boolean {
		return this.itemsToExclude.includes(item);
	}

	public isPreservedItem(item: string): boolean {
		return this.itemsToPreserve.includes(item);
	}

	public makePropertyEditable(ev: MouseEvent): void {
		this.stopEventPropagationDuringEditing(ev);
		if (!this.isAvailableToEdit) {
			return;
		}
		if (this.type === EditItemType.Logo) {
			this.clearValidationErrors('logoBase64');
		}

		this.makeEditableSubject.next({ propertyName: this.propertyName, ev });
		this._reviewEditsService.resetPropertyStatus(this.propertyName);
	}

	public revertEdit(ev: MouseEvent): void {
		this.stopEventPropagationDuringEditing(ev);
		this.revertItemEdit();
	}

	public showValidationError(field: string, key: string): void {
		const ctrl = this.parentForm.get(field);
		const validationErrors: ValidationErrors = {};
		if (key) {
			validationErrors[key] = true;
		}
		ctrl.setErrors(validationErrors);
	}

	public clearValidationErrors(field: string): void {
		this.showValidationError(field, null);
	}

	public async onLogoUpdated(event: Event): Promise<void> {
		const target = event.target as HTMLInputElement;
		const file = target.files[0];

		const recordError = (key: string) => {
			this.showValidationError('logoBase64', key);
		};

		if (!file) {
			// Probably Canceled
			recordError(null);
			return;
		}

		const fileType = file.type && file.type.toLowerCase();
		switch (fileType) {
			case 'image/png':
			case 'image/jpeg':
			case 'image/gif':
				break;
			default:
				// show error in UI
				recordError('badType');
				return;
		}

		if (file.size > maxUploadLogoImageSize) {
			// show error in UI
			recordError('tooLarge');
			return;
		}

		// Take down any old error
		recordError(null);

		const result = await this.toBase64(file).catch((e) => e);
		if (result instanceof Error) {
			return;
		}
		// Will be something like "data:image/jpeg;base64,/9j/4AAQ..." and we only want the image bytes
		const dataPos = result.indexOf(',') + 1;
		const data = result.substring(dataPos);
		this.parentForm.get('logoBase64').setValue(data);
	}

	public get required(): boolean {
		return (
			this.parentForm?.get(this.propertyName)?.errors &&
			this.parentForm?.get(this.propertyName)?.errors['required'] === true
		);
	}

	public get notEditingClass(): string {
		let cssClass = 'inline';

		if (this.isPropertyDeclined) {
			cssClass += ' strikethrough';
		}

		if (this.hasReviewableField) {
			cssClass += ' not-for-review';
		}

		if (this.isJnjConfidentialInfo) {
			cssClass += ' jnj-confidential';
		}

		if (this.isEmptyValue) {
			cssClass += ' empty';
		}

		return cssClass;
	}

	public propertyDecorationClass(isItemGroup?: boolean): string {
		let cssClass = '';
		if (this.hasReviewableField) {
			cssClass = 'pending';
			cssClass += this.isEditingThisProperty ? ' editable' : '';
		} else if (this.isEditMode && this.isEditingThisProperty) {
			cssClass = 'editable';
		} else if (!this.isAvailableToEdit) {
			cssClass = 'readonly';
		}

		const companyToCompare = this.isEditMode
			? this.companyBeforeAnyChanges
			: this.companyWithPending;

		if (
			itemIsDifferent(this.propertyName, this.company, companyToCompare)
		) {
			cssClass += ' different';
		}
		if (!isItemGroup && this.isPropertyDeclined) {
			cssClass += ' strikethrough';
		}

		if (!this.hasReviewableField) {
			cssClass += ' not-for-review';
		}

		if (this.isJnjConfidentialInfo) {
			cssClass += ' jnj-confidential';
			if (this.showFlagIcon) {
				cssClass += ' with-flag';
			}
		}

		if (this.isEmptyValue) {
			cssClass += ' empty';
		}

		return cssClass;
	}

	public hasTooltip(): boolean {
		return !!this.tooltip;
	}

	public toggleTooltip(): void {
		this.isShowingTooltip = this.hasTooltip && !this.isShowingTooltip;
	}

	private revertItemEdit(): void {
		this.revertSubject.next(this.propertyName);
	}

	private toBase64(file: Blob): Promise<string | ArrayBuffer> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
		});
	}

	private lengthSubscription: Subscription;
	private get canLimitLength(): boolean {
		return (
			this.type === EditItemType.Multiline ||
			this.type === EditItemType.Default
		);
	}
	public get badLogoType() {
		return this.parentForm?.get('logoBase64').errors?.badType;
	}

	public get logoTooLarge() {
		return this.parentForm?.get('logoBase64').errors?.tooLarge;
	}
}
